import React, {
	cloneElement,
	forwardRef,
	isValidElement,
	ReactElement,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { createPortal } from "react-dom";
import "./SpoofyCrudPageablePage.scss"
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableSelectionSingleChangeEvent,
	DataTableValueArray
} from "primereact/datatable";
import { tableActionDeleteTemplate, tableHeaderTemplate } from "../DataTableComponents";
import { Column } from "primereact/column";
import i18n from "../../Translations/i18n";
import { IBaseService } from "../../Services/BaseService";
import ApiResponse from "../../Models/ApiResponse";
import { getDefaultPage, Page } from "../../Models/Page";
import { FormProvider, SubmitHandler, useForm, UseFormReturn, type FieldErrors } from "react-hook-form";
import { LabelInputField, DashlyDropdown, DashlyFilterDropdown } from "../inputs";
import { SmartFilterButton } from "../inputs/SmartFilterButton/SmartFilterButton";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import { CrudDialog } from "../Dialogs/AppDialog/CrudDialog";
import { toast } from "react-toastify";
import { classNames } from "primereact/utils";
import secureLocalStorage from "react-secure-storage";
import handleError from "../../Util/HandleError";
import { DataView, DataViewLayoutOptions } from "primereact/dataview";
import { DataViewGridTemplate, DataViewListTemplate } from "../../Models/Crud/DataViewTemplate";
import { AxiosResponse } from "axios";
import { useMenu } from "../Template/MenuBar/MenuContext";
import { usePageHeader } from "../Template/PageTitle/PageContext";
import { Card } from "primereact/card";
import ReferenceModel from "../../Models/ReferenceModel";
import MultiCustomerCheck from "../MultiCustomerCheck";
import WorkflowCustomerTag from "../Custom/WorkflowCustomerTag";
import { CustomerService } from "../../Services/customerService";
import { Dropdown } from "primereact/dropdown";
import { FilterDropdown } from "../inputs/FilterDropdown/FilterDropdown";
import { DashlyMultiSelect } from "../inputs/DashlyMultiSelect/DashlyMultiSelect";
import { DashlyFilterMultiSelect } from "../inputs/DashlyFilterMultiSelect/DashlyFilterMultiSelect";
import { DashlyTextInput } from "../inputs/DashlyTextInput/DashlyTextInput";

const supportsFilterVariant = (component: React.ElementType) =>
	component === DashlyTextInput ||
	component === DashlyDropdown ||
	component === DashlyMultiSelect ||
	component === DashlyFilterMultiSelect;
import { isUndefined } from "node:util";
import { VirtualScrollerLazyEvent } from "primereact/virtualscroller";
import { Skeleton } from "primereact/skeleton";
import { v4 as uuidv4 } from 'uuid';
import { IConversationMessageModel } from "../../Services/ConversationService";
import LoadingPopup from "../../Util/loadingPopup/loadingPopup";

export type FilterClearButtonMode = "perFilter" | "global";

interface SpoofyCrudPageProps<F, C> extends Omit<any, any> {
	actions?: any[];
	crudDialog: CrudDialogProps;
	dialogs?: typeof CrudDialog[];
	permissionName?: string;
	permissionNames?: string[];
	pageTitle: string;
	/** i18n key for list/empty-state entity label; defaults to ``${pageTitle}s`` when omitted. */
	listTypeKey?: string;
	/** Optional i18n key for the global page header subtitle. */
	pageSubtitle?: string;
	/** When false, skips the global Dashly page header (e.g. embedded CRUD). */
	showPageHeader?: boolean;
	setNavigationTitle?: boolean;
	fetchMethod?: any;
	customCustomerSelect?: boolean;
	setMethod?: (value: any) => void;
	onError?: (apiResponse: any, methods: any) => boolean | void;
	onValidationError?: (errors: FieldErrors, methods: UseFormReturn) => void;
	service: IBaseService<F, C>;
	fetchData?: boolean;
	setIsEditMode?: (value: boolean) => void;
	setIsViewMode? : (value: boolean) => void;
	properties: DataTableProps | DataViewProps;
	filter: FilterProps;
	multiCustomer?: MultiCustomerProps
	itemList?: any[]
}

export interface MultiCustomerProps {
	enabled: boolean;
	required: boolean;
	allowEdit: boolean;
}

/** Merges hidden/default criteria; does not overwrite values already submitted from the filter form. */
const mergeFilterInitial = (data: Record<string, unknown>, initial?: Record<string, unknown>) => {
	if (!initial) {
		return;
	}
	for (const [key, value] of Object.entries(initial)) {
		if (data[key] === undefined) {
			data[key] = value;
		}
	}
};

export interface FilterProps {
	enabled: boolean;
	default: boolean;
	/** Default/hidden criteria (e.g. fixed location). Form field values take precedence on submit. */
	initial?: any;
	leftContent?: React.ReactNode;
	rightContent?: React.ReactNode;
	/** When true (default), filter changes apply automatically without clicking search. */
	autoApply?: boolean;
	/** Debounce delay in ms for auto-apply (text inputs). Default 350. */
	autoApplyDebounceMs?: number;
	/**
	 * `perFilter` (default): integrated clear on each filter field.
	 * `global`: single reset control that clears all filters.
	 */
	clearButtonMode?: FilterClearButtonMode;
	/** When set, filter fields render into this DOM element instead of inline above the table card. */
	portalTargetId?: string;
}

export interface CrudDialogProps {
	enabled: boolean;
	initial?: any;
	ref?: any;
	content?: React.ReactNode;
	style?: React.CSSProperties;
	className?: string;
}

export interface DefaultCrudActionProps {
	deleteAction?: boolean;
	editAction?: boolean;
	viewAction?: boolean;
	addAction?: boolean;
}

export interface RowSelectionProps {
	enabled?: boolean;
	selectionMode?: any;
	onChange?: (data: any | any[]) => void;
}

export interface DataTableProps extends DataProps {
	type: "datatable";
	tableActionTemplate?: any;
	columns?: any;
	rowExpansionTemplate?: any;
	defaultCrudActions?: DefaultCrudActionProps;
	rowSelection?: RowSelectionProps;
}

export interface DataViewProps extends DataProps {
	type: "dataview"
	dataViewGridTemplate?: DataViewGridTemplate;
	dataViewListTemplate?: DataViewListTemplate;
}

export interface AdditionalActionProps {
	onAdditionalSaveAction?: (data: OnAdditionalActionProps) => void;
	onAdditionalDeleteAction?: (data: OnAdditionalActionProps) => void;
	onAdditionalEditAction?: (data: OnAdditionalActionProps) => void;
	onAdditionalAddAction?: (data: OnAdditionalActionProps) => void;
	onAdditionalOnDialogOpenAction?: (data: OnAdditionalActionProps) => void;
	onAdditionalOnAddAction?: (data: OnAdditionalActionProps) => void;
	onAdditionalOnHideAction?: (data: OnAdditionalActionProps) => void;
}

export interface OnAdditionalActionProps {
	update?: boolean
	data?: any
}

export interface DataProps {
	type: "datatable" | "dataview"
	paginator: PaginatorProps;
	lazy: boolean;
	additionalActions?: AdditionalActionProps
}

export interface PaginatorProps {
	enabled: boolean;
	options?: number[];
	scrollable?: boolean;
	initialOption?: number;
	/**
	 * Opt-in infinite list. Hides the paginator and appends the next page while scrolling.
	 * Page size stays `initialOption` / `size`. Other Spoofy screens are unchanged unless this is true.
	 */
	infiniteScroll?: boolean;
	/** Fetch the next page when this many rows remain before the bottom. Used with `infiniteScroll`. */
	infiniteScrollOffsetRows?: number;
}

export interface SpoofyCrudPageRef {
	refreshDataTable: () => void,
	availableActions: (item: any) => React.ReactNode[],
	filters: () => {},
}

const SpoofyCrudPage = forwardRef<SpoofyCrudPageRef, SpoofyCrudPageProps<any, any>>(<F, C>({
	properties = {
		lazy: true,
		paginator: {
			enabled: true,
			scrollable: false,
		},
		defaultCrudActions: undefined,
		type: "datatable",
	} as DataTableProps,
	pageTitle,
	listTypeKey,
	pageSubtitle,
	showPageHeader = true,
	setNavigationTitle = false,
	service,
	itemList,
	filter = {
		enabled: true,
		default: true,
		autoApply: true,
		autoApplyDebounceMs: 350,
	},
	actions = [],
	crudDialog = {
		enabled: false,
		initial: undefined,
	},
	fetchMethod,
	setMethod,
	onError,
	onValidationError: onValidationErrorProp,
	dialogs,
	setIsEditMode,
	setIsViewMode,
	permissionName,
	permissionNames,
	customCustomerSelect,
	fetchData,
	multiCustomer = {
		enabled: false,
		required: false,
		allowEdit: false
	},
	...props
}: SpoofyCrudPageProps<F, C>, ref: React.Ref<SpoofyCrudPageRef> | undefined) => {

	const { t } = useTranslation();
	const listTypeLabelKey = listTypeKey ?? `${pageTitle}s`;

	const permissions = secureLocalStorage.getItem("permissions") as any[] || [];

	const [content, setContent] = useState<Page<F> | F[]>(properties.paginator ? getDefaultPage(properties.paginator.initialOption ?? 25) : [])
	const defaultFilterValues = {
		...(filter.default ? { includeInactive: false } : {}),
		...(filter.initial ?? {}),
	};
	const [crudFilter, setCrudFilter] = React.useState<any>(properties.paginator ? {
		page: (content as Page<F>)['number'],
		size: (content as Page<F>)['size'], ...defaultFilterValues
	} : { ...defaultFilterValues });
	const [loading, setLoading] = React.useState(false);
	const spoofyRootRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef(content);
	const loadingRef = useRef(loading);
	const loadingMoreRef = useRef(false);
	contentRef.current = content;
	loadingRef.current = loading;

	const appendPageResult = (responseData: Page<F> | F[]) => {
		if (Array.isArray(responseData)) {
			setContent((previous: any) => [...previous, ...responseData]);
			return;
		}
		setContent((previous: any) => ({
			...responseData,
			content: [
				...(previous.content ?? []),
				...(responseData.content ?? []),
			],
		}));
	};

	const infiniteScrollEnabled = Boolean(properties.paginator?.infiniteScroll);

	const [isMultiCustomer, setIsMultiCustomer] = useState<boolean>(false);
	const [customerSelectItems, setCustomerSelectItems] = useState<ReferenceModel[]>();

	const [selectedItems, setSelectedItems] = useState<F[]>();
	const [crudDialogVisible, setCrudDialogVisible] = React.useState(false);
	const [crudDialogView, setCrudDialogView] = React.useState(true);
	const [crudDialogInitial, setCrudDialogInitial] = React.useState<C>();
	const [crudDialogMethods, setCrudDialogMethods] = React.useState<UseFormReturn | null>(null);

	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | any[] | undefined>();
	const [layout, setLayout] = useState<'list' | 'grid'>('list');

	const { setMenu, resetMenu } = useMenu();
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const [editMode, setEditMode] = useState<boolean>(false);
	const [isViewMode, setViewMode] = useState<boolean>(false);


	const filterFormRef = React.useRef<HTMLFormElement>(null);
	const filterMethods = useForm<any>();
	const filterAutoApplyReadyRef = React.useRef(false);
	const filterDebounceRef = React.useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		if (showPageHeader) {
			const subtitleKey = pageSubtitle && i18n.exists(pageSubtitle)
				? pageSubtitle
				: [`${pageTitle}.subtitle`, `${pageTitle}.page.subtitle`, `${pageTitle}.list.subtitle`]
					.find((key) => i18n.exists(key));
			setPageHeader({
				title: { value: pageTitle, translationKey: true },
				subtitle: subtitleKey ? { value: subtitleKey, translationKey: true } : undefined,
			});
		}

		return () => {
			if (showPageHeader) {
				resetPageHeader();
			}
			if (setNavigationTitle) {
				resetMenu();
			}
		};
	}, [pageSubtitle, pageTitle, resetMenu, resetPageHeader, setNavigationTitle, setPageHeader, showPageHeader]);

	useEffect(() => {
		fetchDataTableData();

		let isMultiCustomer = multiCustomer.enabled && MultiCustomerCheck() as boolean; //Assign to local variable first as I need it immediately
		if (isMultiCustomer) {
			fetchCustomerSelectList();
		}
		setIsMultiCustomer(isMultiCustomer);

		if (filter.initial || filter.default) {
			filterMethods.reset(defaultFilterValues);
		}
	}, []);

	useEffect(() => {
		fetchDataTableData();
	}, [crudFilter]);

	useEffect(() => {
		fetchDataTableData();
	}, [fetchData]);

	useEffect(() => {
		filterAutoApplyReadyRef.current = true;
	}, []);

	useEffect(() => {
		if (!filter.enabled || filter.autoApply === false) {
			return;
		}

		const debounceMs = filter.autoApplyDebounceMs ?? 350;
		const subscription = filterMethods.watch(() => {
			if (!filterAutoApplyReadyRef.current) {
				return;
			}
			if (filterDebounceRef.current) {
				clearTimeout(filterDebounceRef.current);
			}
			filterDebounceRef.current = setTimeout(() => {
				filterMethods.handleSubmit((data) => onFilterSubmit(data))();
			}, debounceMs);
		});

		return () => {
			subscription.unsubscribe();
			if (filterDebounceRef.current) {
				clearTimeout(filterDebounceRef.current);
			}
		};
	}, [filter.enabled, filter.autoApply, filter.autoApplyDebounceMs]);

	const fetchCustomerSelectList = () => {
		new CustomerService().fetchSelectItems().then(response => {
			setCustomerSelectItems((response?.data as ApiResponse<ReferenceModel[]>).data)
		})
	}

	const fetchDataTableData = (firstLoad = true): void => {
		// if (filter.enabled) { //PD-237 removed due to being strange
		if (firstLoad) {
			loadingMoreRef.current = false;
		}
		setLoading(true);
		if (itemList) {
			setContent(itemList);
			setLoading(false);
		} else {
			if (fetchMethod != null) {
				fetchMethod(crudFilter).then((response: AxiosResponse<ApiResponse<Page<F> | F[]>>) => {
					if (!firstLoad && infiniteScrollEnabled && response.data.data) {
						appendPageResult(response.data.data);
					} else {
						setContent(response.data.data);
					}
				}).catch((error: any) => {
					handleError(error)
				}).finally(() => {
					loadingMoreRef.current = false;
					setLoading(false);
				});
			} else {
				service.fetchItems(crudFilter).then((response: AxiosResponse<ApiResponse<Page<F> | F[]>>) => {
					if (!firstLoad && infiniteScrollEnabled && response.data.data) {
						appendPageResult(response.data.data);
					} else {
						setContent(response.data.data);
					}
				}).catch((error) => {
					handleError(error)
				}).finally(() => {
					loadingMoreRef.current = false;
					setLoading(false);
				});
			}
		}
		// }
	}

	useEffect(() => {
		if (!infiniteScrollEnabled) {
			return;
		}

		const wrapper = spoofyRootRef.current?.querySelector<HTMLElement>(".p-datatable-wrapper");
		if (!wrapper) {
			return;
		}

		const offsetRows = properties.paginator.infiniteScrollOffsetRows ?? 5;
		const prefetchPx = offsetRows * 55;
		const reachedEnd = (page: Page<F>) => {
			if (page.last) {
				return true;
			}
			const loaded = page.content?.length ?? 0;
			const total = page.totalElements;
			return typeof total === "number" && loaded >= total;
		};

		const maybeLoadMore = () => {
			const page = contentRef.current as Page<F>;
			if (loadingRef.current || loadingMoreRef.current || reachedEnd(page)) {
				return;
			}
			const remaining = wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight;
			if (remaining > prefetchPx) {
				return;
			}
			loadingMoreRef.current = true;
			crudFilter.page = (page.number ?? 0) + 1;
			crudFilter.size = page.size || properties.paginator.initialOption || (content as Page<F>).size;
			fetchDataTableData(false);
		};

		wrapper.addEventListener("scroll", maybeLoadMore, { passive: true });
		maybeLoadMore();
		return () => wrapper.removeEventListener("scroll", maybeLoadMore);
	}, [content, infiniteScrollEnabled, properties.paginator?.infiniteScrollOffsetRows, properties.paginator?.initialOption]);

	// Expose fetchDataTableData to parent components
	useImperativeHandle(ref, () => ({
		refreshDataTable: fetchDataTableData,
		availableActions: availableActions,
		filters: retrieveFilters,
	}));

	const retrieveFilters = () => {
		return filterMethods.getValues();
	}

	const onScrollLazyLoad = (event: any) => {
		if (event != null && event.first != null && event.rows != null) {
			crudFilter.page = Math.floor(event.first / event.last);
			crudFilter.size = event.last;
			(content as Page<F>).first = false;

			const totalRecords = (content as Page<F>).totalElements ?? 0;

			if ((crudFilter.page * crudFilter.size) < totalRecords) {
				fetchDataTableData(false);
			}
		}
	};


	const onPageEvent = (event: any) => {
		if (event != null && event.page != null && event.rows != null) {
			crudFilter.page = (content as Page<F>).number = event.page;
			crudFilter.size = (content as Page<F>).size = event.rows;
			(content as Page<F>).first = false;
		}

		fetchDataTableData(true)
	};

	const onFilterSubmit: SubmitHandler<any> = (data) => {
		if (properties.paginator.enabled) {
			data.page = 0;
			data.size = (content as Page<F>).size;
		}
		mergeFilterInitial(data, filter.initial);

		setCrudFilter(data);
	}

	const onFilterReset = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (filterDebounceRef.current) {
			clearTimeout(filterDebounceRef.current);
		}
		filterMethods.reset(defaultFilterValues);
		filterMethods.handleSubmit((data) => onFilterSubmit(data))();
	}

	const resolveDefaultClearValue = (childProps: Record<string, unknown>): unknown => {
		if (childProps.defaultValue !== undefined) {
			return childProps.defaultValue;
		}
		if (childProps.component === Checkbox) {
			return false;
		}
		if (
			childProps.component === Dropdown ||
			childProps.component === DashlyDropdown ||
			childProps.component === DashlyFilterDropdown ||
			childProps.component === FilterDropdown ||
			childProps.filterNativeSelect === true ||
			childProps.component === DashlyFilterDropdown ||
			childProps.component === FilterDropdown
		) {
			return null;
		}
		if (childProps.component === DashlyMultiSelect || childProps.component === DashlyFilterMultiSelect) {
			return [];
		}
		return "";
	};

	const resolveFilterPlaceholder = (childProps: Record<string, unknown>): string | undefined => {
		if (childProps.placeholder) {
			return String(childProps.placeholder);
		}
		if (!childProps.label) {
			return undefined;
		}
		const label = String(childProps.label);
		return childProps.filterSearch ? `${label}...` : label;
	};

	const filterClearButtonMode = filter.clearButtonMode ?? "perFilter";
	const [filterPortalTarget, setFilterPortalTarget] = useState<HTMLElement | null>(null);

	useLayoutEffect(() => {
		if (!filter.portalTargetId) {
			setFilterPortalTarget(null);
			return;
		}
		setFilterPortalTarget(document.getElementById(filter.portalTargetId));
	}, [filter.portalTargetId, filter.enabled, filter.leftContent, filter.rightContent]);

	const enhanceFilterField = (child: React.ReactNode): React.ReactNode => {
		if (!isValidElement(child)) {
			return child;
		}

		if (child.type === SmartFilterButton || child.type === Button) {
			return child;
		}

		const useLegacyNativeSelectFlag = child.props.filterNativeSelect === true;
		const isDropdownFilter =
			useLegacyNativeSelectFlag ||
			child.props.component === Dropdown ||
			child.props.component === DashlyDropdown ||
			child.props.component === DashlyFilterDropdown ||
			child.props.component === FilterDropdown;

		const isToggleFilter =
			child.props.component === Checkbox ||
			child.props.component === InputSwitch;

		const placeholder = isToggleFilter
			? undefined
			: resolveFilterPlaceholder(child.props);

		const defaultClearValue = resolveDefaultClearValue(child.props);

		const shouldShowPerFieldClear =
			filterClearButtonMode === "perFilter" &&
			Boolean(child.props.name) &&
			!child.props.filterHideClear &&
			child.props.component !== Checkbox &&
			child.props.component !== InputSwitch;

		const field = cloneElement(child as ReactElement, {
			pageFilter: child.props.pageFilter ?? true,
			placeholder,
			component: isDropdownFilter ? DashlyFilterDropdown : child.props.component,
			...(supportsFilterVariant(child.props.component) && !isDropdownFilter
				? { controlVariant: child.props.controlVariant ?? "filter" }
				: {}),
			...(child.props.component === DashlyTextInput
				? { debounceMs: child.props.debounceMs ?? 0 }
				: {}),
			containerClassName: classNames(
				"spoofy-filter-field",
				isToggleFilter && "spoofy-filter-field--toggle",
				!child.props.filterSearch && child.props.containerClassName
			),
			labelClassName: isToggleFilter
				? "spoofy-filter-field__label spoofy-filter-field__label--visible"
				: (child.props.labelClassName ?? "spoofy-filter-field__label"),
			inputClassName: isToggleFilter
				? "spoofy-filter-field__toggle-control"
				: (child.props.inputClassName ?? "spoofy-filter-field__control"),
			...(isToggleFilter ? { inputId: child.props.name } : {}),
			...(isDropdownFilter
				? {
					panelClassName: classNames(
						"spoofy-filter-dropdown-panel",
						child.props.panelClassName,
					),
					appendTo:
						child.props.appendTo ?? (typeof document !== "undefined" ? document.body : undefined),
					showClear: false,
					filter: child.props.filter ?? true,
					editable: child.props.editable,
				}
				: {}),
			...(shouldShowPerFieldClear
				? {
					showClear: child.props.showClear ?? true,
					clearValue: defaultClearValue,
					clearVariant: isDropdownFilter ? "select" : "text",
					clearAriaLabel: t("common.action.clear_filter"),
					onClear: () => {
						if (filterDebounceRef.current) {
							clearTimeout(filterDebounceRef.current);
						}
						filterMethods.handleSubmit((data) => onFilterSubmit(data))();
					},
				}
				: {}),
		});

		let content: React.ReactNode = field;

		if (child.props.filterSearch) {
			return (
				<div className="spoofy-filter-field spoofy-filter-field--search">
					{cloneElement(content as ReactElement, {
						leadingIcon: child.props.leadingIcon ?? "pi pi-search",
						controlVariant: child.props.controlVariant ?? "filter",
					})}
				</div>
			);
		}

		return content;
	};

	const onValidationError = (errors: FieldErrors, methods: UseFormReturn) => {
		toast.error(t('validation.form-has-errors'), { autoClose: 2500 });
		const firstErrorField = Object.keys(errors)[0];
		if (firstErrorField) {
			void methods.setFocus(firstErrorField as Parameters<UseFormReturn['setFocus']>[0]);
		}
		onValidationErrorProp?.(errors, methods);
	};

	const handleSaveError = (ex: any, methods: UseFormReturn) => {
		const apiResponse = ex?.response?.data as ApiResponse<any> | undefined;
		const errorKey = apiResponse?.message ?? apiResponse?.internalCode;
		if (errorKey) {
			const handled = onError?.({ ...apiResponse, message: errorKey }, methods) === true;
			if (!handled) {
				toast.error(t(errorKey));
			}
		} else {
			toast.error(t('common.failed'));
		}
		if (ex?.response?.status?.toString().startsWith("5")) {
			handleError(ex);
		}
	};

	const onSaveAction = (data: any, methods: UseFormReturn) => {
		if (data.uuid == null) {
			service.postItem(data).then(response => {
				fetchDataTableData();
				setCrudDialogVisible(false);
				if (properties.additionalActions?.onAdditionalSaveAction !== null && properties.additionalActions?.onAdditionalSaveAction !== undefined) {
					properties.additionalActions?.onAdditionalSaveAction({
						update: false,
						data: response.data.data
					} as OnAdditionalActionProps)
				}
				toast.success(t('common.saved'), { autoClose: 1500 })
			}).catch(ex => {
				handleSaveError(ex, methods);
			})
		} else {
			service.putItem(data.uuid, data).then(response => {
				fetchDataTableData();
				setCrudDialogVisible(false);
				if (properties.additionalActions?.onAdditionalSaveAction !== null && properties.additionalActions?.onAdditionalSaveAction !== undefined) {
					properties.additionalActions?.onAdditionalSaveAction({
						update: true,
						data: response.data.data
					} as OnAdditionalActionProps)
				}
				toast.success(t('common.saved'), { autoClose: 1500 })
			}).catch(ex => {
				handleSaveError(ex, methods);
			})
		}

	}

	const onHideAction = () => {
		// Reset the form when dialog closes
		if (crudDialogMethods) {
			crudDialogMethods.reset({}, { keepValues: false });
		}

		setCrudDialogVisible(false);

		if (properties.additionalActions?.onAdditionalOnHideAction !== null && properties.additionalActions?.onAdditionalOnHideAction !== undefined) {
			properties.additionalActions?.onAdditionalOnHideAction({
				update: false,
				data: undefined
			} as OnAdditionalActionProps);
		}

	}

	const onAddAction = () => {
		if (properties.additionalActions?.onAdditionalAddAction) {
			properties.additionalActions.onAdditionalAddAction({
				update: false,
				data: undefined,
			} as OnAdditionalActionProps);
			return;
		}

		setCrudDialogView(false)
		setCrudDialogInitial(crudDialog.initial ?? {} as C)

		if (properties.additionalActions?.onAdditionalOnAddAction !== null && properties.additionalActions?.onAdditionalOnAddAction !== undefined) {
			properties.additionalActions?.onAdditionalOnAddAction({
				update: false,
				data: undefined
			} as OnAdditionalActionProps);
		}

		setCrudDialogVisible(true);
		if (setIsEditMode) {
			setIsEditMode(false);
		} if (setIsViewMode) {
			setIsViewMode(false)
			setViewMode(false)
		}


		setEditMode(false);
	}

	const onViewAction = (uuid: string) => {
		// set viewOnly to true;
		setCrudDialogView(true);
		
		onOpenDialogAction(uuid); 
		if (setIsViewMode) {
			setIsViewMode(true)
			setViewMode(true)

		} if (setIsEditMode) {
			setIsEditMode(true);
		}
	}

	const onEditAction = (uuid: string) => {
		if (properties.additionalActions?.onAdditionalEditAction) {
			properties.additionalActions.onAdditionalEditAction({ update: true, data: { uuid } });
			return;
		}

		// set viewOnly to true;
		if (setIsEditMode) {
			setIsEditMode(true);
		} if (setIsViewMode) {
			setIsViewMode(false)
			setViewMode(false)
		}

		setCrudDialogView(false)
		onOpenDialogAction(uuid);
		setEditMode(true);
	}

	const onDeleteAction = (event: any, uuid: string) => {
		tableActionDeleteTemplate(event, () => {
			let loadingToastId = toast.loading(t('common.saving'));
			service.deleteItem(uuid).then(r => {
				fetchDataTableData();
				if (properties.additionalActions?.onAdditionalDeleteAction) {
					properties.additionalActions.onAdditionalDeleteAction({ update: false, data: uuid });
				}
				toast.update(loadingToastId, { type: "success", render: t('common.saved'), isLoading: false, autoClose: 1500 })
			}).catch((error) => {
				handleError(error)
				toast.update(loadingToastId, { type: "error", render: t('common.failed'), isLoading: false, autoClose: 1500 })
			})
		});
	}

	const onOpenDialogAction = (uuid: string) => {
		service.fetchItem(uuid).then(response => {
			let apiResponse = response?.data as ApiResponse<C>;

			setCrudDialogInitial(apiResponse.data);
			setCrudDialogVisible(true);

			// Defer enrich until after CrudDialog is visible and form methods are wired
			if (properties.additionalActions?.onAdditionalOnDialogOpenAction !== null && properties.additionalActions?.onAdditionalOnDialogOpenAction !== undefined) {
				window.setTimeout(() => {
					properties.additionalActions?.onAdditionalOnDialogOpenAction?.({
						update: true,
						data: apiResponse.data
					} as OnAdditionalActionProps);
				}, 0);
			}
		}).catch(response => {
			let apiResponse = response?.data as ApiResponse<C>;
		});
	}

	const determineIfPermissionIsPresent = (suffix: string) => {

		if (permissionName !== undefined) {
			return permissions.includes(permissionName + suffix)
		}

		if (permissionNames !== undefined && permissionNames.length !== 0) {

			for (let permName of permissionNames) {
				if (permissions.includes(permName + suffix)) {
					return true;
				}
			}
		}

		return false;
	}

	const pageFooter = () => {
		const t = i18n.t;

		let pageActions = [];
		if (actions !== null && actions !== undefined && !(actions instanceof Array)) {
			return undefined;
		} else if (actions !== null && actions !== undefined && actions.length > 0) {
			pageActions.push(actions);
		}
		const hasCustomAddAction = Boolean(properties.additionalActions?.onAdditionalAddAction);
		const defaultAddAction = properties.type === "datatable"
			? (properties.defaultCrudActions?.addAction ?? true)
			: false;
		const showAddAction = defaultAddAction
			&& determineIfPermissionIsPresent(".Add")
			&& (crudDialog.enabled || hasCustomAddAction);
		if (properties.type === "datatable" && showAddAction) {
			pageActions.push(<Button onClick={(event) => {
				event.preventDefault();
				event.stopPropagation();
				onAddAction();
			}}>{t("common.add")}</Button>);
		}

		if (!pageActions || pageActions.length === 0) return undefined;
		return React.Children.map(pageActions, child => {
			if (!isValidElement(child)) return null; // Ensure child is a valid ReactElement
			return cloneElement(child as ReactElement, { key: child.key, className: classNames('ml-1') });
		})

		// if (actions != null && actions.length > 0) return actions + crudDialogContent != null ? <Button onClick={onAddAction}>{t("common.add")}</Button> : <></>;
		// return crudDialogContent != null ? <Button onClick={onAddAction}>{t("common.add")}</Button> : <></>;
	}

	const userHasAnyPermission = () => {
		return determineIfPermissionIsPresent(".View") || determineIfPermissionIsPresent(".Edit") || determineIfPermissionIsPresent(".Delete");
	}

	const tableActionBodyTemplate = (rowData: any) => {
		return (
			<div className="spoofy-table-actions">
				{availableActions(rowData)}
			</div>
		);
	};

	const onSelectionChange = (event: DataTableSelectionSingleChangeEvent<any>) => {
		setSelectedItems(event.value);
		if (properties.type === "datatable" && properties.rowSelection && properties.rowSelection.onChange) {
			properties.rowSelection?.onChange(event.value);
		}
	}

	const availableActions = (rowData: any): React.ReactNode[] => {
		if (properties.type !== "datatable") {
			return [];
		}

		const actions: React.ReactNode[] = [];

		if (properties.tableActionTemplate != null) {
			actions.push(
				<React.Fragment key={`${rowData.uuid}-custom`}>
					{properties.tableActionTemplate(rowData)}
				</React.Fragment>
			);
		}

		if ((properties.defaultCrudActions?.viewAction ?? true) && crudDialog.enabled && determineIfPermissionIsPresent(".View")) {
			actions.push(
				<Button
					key={`${rowData.uuid}-view`}
					type="button"
					icon="pi pi-eye"
					text
					className="mr-2"
					onClick={(event) => { event.preventDefault(); event.stopPropagation(); onViewAction(rowData.uuid); }}
					tooltip={t("common.action.view")}
					tooltipOptions={{ position: 'bottom', appendTo: typeof document !== 'undefined' ? document.body : undefined }}
				/>
			);
		}

		if ((properties.defaultCrudActions?.editAction ?? true)
			&& (crudDialog.enabled || Boolean(properties.additionalActions?.onAdditionalEditAction))
			&& determineIfPermissionIsPresent(".Edit")) {
			actions.push(
				<Button
					key={`${rowData.uuid}-edit`}
					type="button"
					icon="pi pi-pencil"
					text
					className="mr-2"
					onClick={(event) => { event.preventDefault(); event.stopPropagation(); onEditAction(rowData.uuid); }}
					tooltip={t("common.action.edit")}
					tooltipOptions={{ position: 'bottom', appendTo: typeof document !== 'undefined' ? document.body : undefined }}
				/>
			);
		}

		if ((properties.defaultCrudActions?.deleteAction ?? true) && determineIfPermissionIsPresent(".Delete")) {
			actions.push(
				<Button
					key={`${rowData.uuid}-delete`}
					type="button"
					icon="pi pi-trash"
					text
					severity="danger"
					onClick={(event) => { event.preventDefault(); event.stopPropagation(); onDeleteAction(event, rowData.uuid); }}
					tooltip={t("common.action.delete")}
					tooltipOptions={{ position: 'left', appendTo: typeof document !== 'undefined' ? document.body : undefined }}
				/>
			);
		}

		return actions;
	};

	const itemTemplate = (item: any, layout?: 'list' | 'grid') => {
		if (!item || properties.type === "datatable") {
			return;
		}

		if (layout === 'list') {
			return (
				<div className={classNames("col-12", !properties.dataViewListTemplate?.items ? 'bg-red-500 text-white' : '')}>
					{properties.dataViewListTemplate && properties.dataViewListTemplate.items(item)}
					{!properties.dataViewListTemplate?.items &&
						<div className="col-12">No template specified for type: {layout}</div>}
				</div>
			)
		} else if (layout === 'grid') {
			return (
				<div
					className={classNames(properties.dataViewGridTemplate?.className, !properties.dataViewGridTemplate?.items ? 'bg-red-500 text-white' : '')}>
					{properties.dataViewGridTemplate?.items && properties.dataViewGridTemplate?.items(item)}
					{!properties.dataViewGridTemplate?.items && <div>No template specified for type: {layout}</div>}
				</div>
			)
		}
	};

	const loadingTemplate = (options: any) => {
		return (
			<div key={uuidv4()} className="flex align-items-center" style={{ height: '17px', flexGrow: '1', overflow: 'hidden' }}>
				<Skeleton width="1rem" height="1rem" />
			</div>
		);
	};

	const listTemplate = (items: any[], layout?: 'grid' | 'list'): React.ReactNode[] | undefined => {
		if (properties.type === "datatable") {
			return;
		}

		return [
			layout === 'list' && properties.dataViewListTemplate?.headers ?
				<div className="font-bold">{(properties.dataViewListTemplate?.headers())}</div> : <></>,
			<div className="grid grid-nogutter">{items.map((item: any) => itemTemplate(item, layout))}</div>
		]
	};

	const filterFieldsContent = (
		<>
			{filter.leftContent &&
				React.Children.map(filter.leftContent, (child) =>
					child == null || child === false ? null : enhanceFilterField(child)
				)}

			{isMultiCustomer && enhanceFilterField(
				<LabelInputField
					label={t("user.customer")}
					name="customer"
					filter={true}
					filterPlaceholder={t("common.search")}
					pageFilter={true}
					component={DashlyDropdown}
					options={customerSelectItems}
					optionLabel="name"
					optionValue="uuid"
					showClear
				/>
			)}

			{filter.default && (
				<SmartFilterButton
					key="filter-include-inactive"
					name="includeInactive"
					label={t("common.include-inactive")}
				/>
			)}

			{filter.rightContent &&
				React.Children.map(filter.rightContent, (child) =>
					child == null || child === false ? null : enhanceFilterField(child)
				)}
		</>
	);

	const filterBar = filter.enabled ? (
		<div className="spoofy-filters">
			<FormProvider {...filterMethods}>
				<form
					onSubmit={filterMethods.handleSubmit((data) => onFilterSubmit(data))}
					className="spoofy-filters__form"
					ref={filterFormRef}
				>
					<div className="spoofy-filters__fields">
						{filterFieldsContent}
					</div>

					{(filter.autoApply === false || filterClearButtonMode === "global") && (
						<div className="spoofy-filters__actions">
							{filter.autoApply === false && (
								<Button
									type="submit"
									icon="pi pi-search"
									rounded
									text
									outlined
									tooltip={t("common.action.search")}
									tooltipOptions={{ position: "bottom" }}
								/>
							)}
							{filterClearButtonMode === "global" && (
								<Button
									type="button"
									icon="pi pi-times"
									rounded
									text
									outlined
									onClick={onFilterReset}
									tooltip={t("common.action.reset")}
									tooltipOptions={{ position: "bottom" }}
								/>
							)}
						</div>
					)}
				</form>
			</FormProvider>
		</div>
	) : null;

	const portaledFilterFields = filter.enabled && filter.portalTargetId && filterPortalTarget
		? createPortal(
			<FormProvider {...filterMethods}>
				<form
					onSubmit={filterMethods.handleSubmit((data) => onFilterSubmit(data))}
					className="spoofy-filters__portal-form"
					ref={filterFormRef}
				>
					{filterFieldsContent}
					{(filter.autoApply === false || filterClearButtonMode === "global") && (
						<div className="spoofy-filters__actions spoofy-filters__actions--portal">
							{filter.autoApply === false && (
								<Button
									type="submit"
									icon="pi pi-search"
									rounded
									text
									outlined
									tooltip={t("common.action.search")}
									tooltipOptions={{ position: "bottom" }}
								/>
							)}
							{filterClearButtonMode === "global" && (
								<Button
									type="button"
									icon="pi pi-times"
									rounded
									text
									outlined
									onClick={onFilterReset}
									tooltip={t("common.action.reset")}
									tooltipOptions={{ position: "bottom" }}
								/>
							)}
						</div>
					)}
				</form>
			</FormProvider>,
			filterPortalTarget,
		)
		: null;

	// @ts-ignore
	// @ts-ignore
	// @ts-ignore
	return (
		<div className="spoofy spoofy-page" ref={spoofyRootRef}>
			{filter.enabled && !filter.portalTargetId && filterBar}
			{portaledFilterFields}

			<Card className="spoofy-table-card">
				{properties.type === "datatable" ? (
					<DataTable value={properties.paginator ? ((content as Page<F>).content as DataTableValueArray) : (content as DataTableValueArray)}
						loading={loading}
						selection={properties.rowSelection?.enabled ? selectedItems : undefined}
						selectionMode={properties.rowSelection?.enabled ? properties.rowSelection.selectionMode ?? 'single' : undefined}
						onSelectionChange={onSelectionChange}
						dataKey="uuid"
						lazy={properties.lazy}
						paginator={properties.paginator.enabled && !properties.paginator.scrollable && !infiniteScrollEnabled}

						scrollable={properties.paginator.scrollable ?? false}
						virtualScrollerOptions={properties.paginator.scrollable ? {
							lazy: true,
							onLazyLoad: onScrollLazyLoad,
							delay: 150,
							showLoader: true,
							itemSize: 55,
							loading: loading,
						} : undefined}
						scrollHeight={properties.paginator.scrollable ? "400px" : undefined}
						// TODO loading translation en wat groter maken.
						loadingIcon={<p className="loading"><i className="pi pi-spinner pi-spin" ></i>{t("common.loading")}</p>}
						className="dataTable striped"
						rows={properties.paginator && !properties.paginator.scrollable && !infiniteScrollEnabled ? (content as Page<F>).size : undefined}
						rowsPerPageOptions={properties.paginator?.enabled && !properties.paginator.scrollable && !infiniteScrollEnabled ? properties.paginator.options ?? [10, 25, 50, 100, 200] : undefined}
						stripedRows
						rowExpansionTemplate={properties.rowExpansionTemplate}
						expandedRows={expandedRows}
						onRowToggle={(e) => setExpandedRows(e.data)}
						first={properties.paginator ? (content as Page<F>).number * (content as Page<F>).size : undefined}
						totalRecords={properties.paginator ? (content as Page<F>).totalElements : undefined}
						onPage={properties.paginator && !properties.paginator.scrollable && !infiniteScrollEnabled ? onPageEvent : undefined}
						paginatorTemplate={properties.paginator ? "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport" : undefined}
						emptyMessage={t('datatable.filter.empty-table', { "type": t(listTypeLabelKey) })}
						currentPageReportTemplate={properties.paginator ? t('datatable.filter.page-report', {
							"type": t(listTypeLabelKey),
							"first": "{first}",
							"last": "{last}",
							"totalRecords": "{totalRecords}"
						}) : undefined}>



						{isMultiCustomer && (
							<Column
								field="customer.name"
								header={t('customer')}
								style={{ width: '10rem' }}
								bodyClassName="spoofy-customer-cell"
								body={(rowData: F) => (
									<WorkflowCustomerTag
										customer={(rowData as { customer?: ReferenceModel }).customer}
										isMultiCustomer={isMultiCustomer}
										truncate
									/>
								)}
							/>
						)}

						{React.Children.map(properties.columns, (child) => {
							if (!isValidElement(child)) {
								return null;
							}
							return cloneElement(child, { key: child.key ?? uuidv4() });
						})}

						{((crudDialog.enabled
							|| properties.tableActionTemplate
							|| properties.additionalActions?.onAdditionalEditAction
							|| properties.additionalActions?.onAdditionalAddAction) && userHasAnyPermission()) &&
							<Column key="actions" className="actions" header={t('common.actions')} body={tableActionBodyTemplate} />
						}

					</DataTable>
				) : (
					<DataView value={properties.paginator ? (content as Page<F>).content : content as any[]}
						lazy={properties.lazy}
						dataKey="uuid" className="dataTable"
						paginator={properties.paginator.enabled}
						layout={layout}
						first={properties.paginator ? (content as Page<F>).number * (content as Page<F>).size : undefined}
						totalRecords={properties.paginator ? (content as Page<F>).totalElements : undefined}
						onPage={properties.paginator ? onPageEvent : undefined}
						rows={properties.paginator ? (content as Page<F>).size : undefined}
						rowsPerPageOptions={properties.paginator?.enabled ? properties.paginator.options ?? [10, 25, 50, 100, 200] : undefined}
						header={tableHeaderTemplate(listTypeLabelKey, fetchDataTableData, <DataViewLayoutOptions className="gap-2" layout={layout} onChange={(e) => setLayout(e.value as 'grid' | 'list')} />)}
						listTemplate={listTemplate}
						paginatorTemplate={properties.paginator ? "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport" : undefined}
						emptyMessage={t('datatable.filter.empty-table', { "type": t(listTypeLabelKey) })}
						currentPageReportTemplate={properties.paginator ? t('datatable.filter.page-report', {
							"type": t(listTypeLabelKey),
							"first": "{first}",
							"last": "{last}",
							"totalRecords": "{totalRecords}"
						}) : undefined}>

					</DataView>
				)}
			</Card>

			{pageFooter() && (
				<div className="flex justify-content-end gap-3 mt-4">
					{pageFooter()}
				</div>
			)}

			{crudDialog.enabled && (
				<CrudDialog title={`${t(pageTitle)} ${t('form')}`}
					className={classNames('dialogForm', crudDialog.className)}
					visible={crudDialogVisible}
					onSubmit={onSaveAction}
					onValidationError={onValidationError}
					view={crudDialogView}
					initialValues={crudDialogInitial}
					style={{ minWidth: '500px', ...(crudDialog.style ?? {}) }}
					ref={crudDialog.ref}
					setMethod={(methods) => {
						setCrudDialogMethods(methods);
						if (setMethod) {
							setMethod(methods);
						}
					}}
					onHide={onHideAction}>

					{isMultiCustomer && !customCustomerSelect && (
						<LabelInputField
							label={t('user.customer')}
							filter={true}
							filterPlaceholder={t('common.search')}
							name="customer"
							component={DashlyDropdown}
							options={customerSelectItems}
							optionLabel="name"
							showClear
							disable={crudDialogView || (!multiCustomer.allowEdit && Boolean((crudDialogInitial as { uuid?: string | null } | undefined)?.uuid))}
							rules={{
								required: multiCustomer.required ? t('validation.required') : false,
							}}
						/>
					)}

					{React.Children.map(crudDialog.content, (child: React.ReactNode) => {
						if (!isValidElement(child)) {
							return null;
						}
						const element = child as ReactElement<{ disable?: boolean }>;
						return cloneElement(element, {
							disable: (element.props.disable && !crudDialogView) || crudDialogView,
						});
					})}
				</CrudDialog>
			)}
		</div>
	);
});

export {
	SpoofyCrudPage
}